/**
 * プラン情報のStateを扱うモジュール
 */
import { ThunkDispatch } from 'redux-thunk';
import { AppState } from 'pages';
import {
  beginFetch,
  endFetch,
  failedFetch,
  FetchStatus
} from 'common/modules/app/fetch';
import {
  fetchFailure,
  ErrorResponse,
  clearErrors
} from 'common/modules/app/errors';
import { ActionsUnion, createAction } from 'common/utils/action-utils';
import {
  getPlanListRequest,
  getPlanRequest
} from 'common/utils/request/sas-request';
import {
  GetPlanListResponse,
  GetPlanResponse
} from 'common/utils/request/sas-response';
import { request } from 'common/utils/request/http-request';
import { VALUE_DUMMY_REGION } from 'common/utils/cookie-utils';

/** Actionとは別で共通関数を定義
 * プラン一覧の絞り込み関数
 * @param list: 絞り込みの元となるデータ
 * @param value: 絞り込みの条件になる値(urlのクエリパラメータ、プルダウンで選択された値。cookieからmodelNameの値を取得する際に取得できなかった場合は空文字を返すような処理になっているためvalueがnullになることはない)
 * @param typeValue: 絞り込みの条件になる値(urlのクエリパラメータ、type。cookieからtypeの値を取得する際に取得できなかった場合は空文字を返すような処理になっているためtypeValueがnullになることはない)
 */
export const filterList = (
  list: PlanState,
  value: string,
  typeValue: string
) => {
  let newState: PlanState = {};
  if (value && value === 'all') {
    // カメラ有償機能のみに絞り込む(deviceLimitTypeが2)
    newState = Object.fromEntries(
      Object.entries(list).filter(
        ([key, plan]) => plan.deviceLimitType && plan.deviceLimitType === 2
      )
    );
  } else if (value !== '' && value !== 'all') {
    // pIdで絞り込み
    newState = Object.fromEntries(
      Object.entries(list).filter(
        ([key, plan]) =>
          plan.devices &&
          plan.devices.some(
            (model) => model.toUpperCase() === value.toUpperCase()
          )
      )
    );
  } else if (typeValue && typeValue === 'app') {
    // カメラ有償機能以外のみに絞り込む(deviceLimitTypeがnull,1)
    newState = Object.fromEntries(
      Object.entries(list).filter(([key, plan]) => plan.deviceLimitType !== 2)
    );
  } else if (typeValue !== '' && typeValue !== 'app') {
    // typeにapp以外の値が指定された場合該当プランなしとする
    newState = {};
  } else {
    // 全プラン
    newState = list;
  }
  return newState;
};

enum ActionTypes {
  RESET_STATE = 'domain/plan/RESET_STATE',
  FETCH_SUCCESS = `domain/plan/FETCH_SUCCESS`,
  FILTER_STATE = 'domain/plan/FILTER_STATE'
}

export enum PlanStateType {
  // 有効
  TYPE_ACTIVE = 'active',
  // 無効
  TYPE_INACTIVE = 'inactive',
  // 新規購入停止
  TYPE_STOP_UPDATE = 'stop',
  // 全て
  TYPE_ALL = 'all'
}

export enum ServiceStateType {
  // 有効
  TYPE_ACTIVE = 'active',
  // 無効
  TYPE_DECTIVE = 'deactive',
  // 全て
  TYPE_ALL = 'all'
}

export enum Period {
  NONE = 0,
  MONTH = 30,
  YEAR = 365
}

export enum ServiceIdType {
  // 全プランを取得する
  TYPE_ALL_PLAN = ''
}

export const GET_PLAN_LIST_ID = 'get_plan_list';
export const GET_PLAN_ID = 'get_plan';

/**
 * プラン情報ををクリアする
 */
export const resetState = () => createAction(ActionTypes.RESET_STATE);

/**
 * プラン情報をを置き換える
 */
export const fetchSuccess = (
  list: PlanInformation[],
  modelName: string,
  type: string
) => {
  // PlanInformation[]をPlanStateに変換してActionを作成
  let planState: PlanState = {};
  for (const data of list) {
    planState = { ...planState, [data.planId]: data };
  }
  const customPayload = {
    planState: planState,
    modelName: modelName,
    type: type
  };
  return createAction(ActionTypes.FETCH_SUCCESS, customPayload);
};

/**
 * URLにクエリパラメータが指定されている場合プラン情報を置き換える
 * initialPlanListは置き換えない
 */
export const filterState = (modelName: string, type: string) => {
  const customPayload = {
    modelName: modelName,
    type: type
  };
  return createAction(ActionTypes.FILTER_STATE, customPayload);
};

const actions = {
  resetState,
  fetchSuccess,
  filterState
};
type PlanAction = ActionsUnion<typeof actions>;

/**
 * プラン情報リストを取得する
 * @param country: 国
 * @param serviceId: サービスID
 * @param language: 言語
 * @param state: 状態
 * @param modelName: 絞り込みの条件になる値(urlのクエリパラメータ、model)
 * @param type: 絞り込みの条件になる値(urlのクエリパラメータ、type)
 */
export const getPlanList = (
  country: string,
  serviceId: string,
  language: string,
  state: PlanStateType,
  modelName: string,
  type: string
) => {
  return async (
    dispatch: ThunkDispatch<Record<string, unknown>, void, any>,
    getState: () => AppState
  ) => {
    // キャンセルを判断する処理を定義
    const isCancel = () => {
      const status = getState().app.fetch[GET_PLAN_LIST_ID];
      return status === FetchStatus.CANCEL;
    };
    // GET_PLAN_LIST_IDを処理中に変更
    dispatch(beginFetch(GET_PLAN_LIST_ID));

    // ErrorStateがプラン情報リスト取得だった場合はリセットする
    if (getState().app.error.requestId === GET_PLAN_LIST_ID) {
      dispatch(clearErrors());
    }

    let nextKey = '';
    do {
      // プラン情報一覧取得APIを実行
      const requestData = getPlanListRequest(
        country,
        language,
        serviceId,
        state,
        nextKey
      );
      const response = await request(
        requestData.url,
        requestData.config,
        false,
        false
      );
      // API実行結果がエラーかどうか判断
      if (response.error) {
        // エラー処理
        const error = response.error as ErrorResponse;
        error.requestId = GET_PLAN_LIST_ID;
        dispatch(fetchFailure(error));
        dispatch(failedFetch(GET_PLAN_LIST_ID));
        return;
      }
      // APIが成功した場合は取得したプラン情報でstateを更新
      const body: GetPlanListResponse = (response.body as unknown) as GetPlanListResponse;
      // 取得したプラン情報でstateを更新
      // キャンセル状態の場合は反映させない
      if (!isCancel()) {
        dispatch(fetchSuccess(body.plan, modelName, type));
      }
      // nextKeyの有無、キャンセルの有無を判断
      if (body.nextKey === '' || isCancel()) {
        // キャンセル又は次の取得が無いため処理を抜ける
        nextKey = '';
      } else {
        // nextKeyを更新
        nextKey = body.nextKey;
      }
    } while (nextKey);
    // GET_PLAN_LIST_IDを未処理に変更
    dispatch(endFetch(GET_PLAN_LIST_ID));
  };
};

/**
 * プラン情報を取得する
 * @param planId: プランID
 * @param country: 国
 * @param serviceId: サービスID
 * @param language: 言語
 * @param state: 状態
 * @param modelName: 絞り込みの条件になる値(urlのクエリパラメータ、model)
 * @param type: 絞り込みの条件になる値(urlのクエリパラメータ、type)
 */
export const getPlan = (
  planId: string,
  country: string,
  language: string,
  state: string,
  serviceId: string,
  modelName: string,
  type: string
) => {
  return async (
    dispatch: ThunkDispatch<Record<string, unknown>, void, any>,
    getState: () => AppState
  ) => {
    // GET_PLAN_IDを処理中に変更
    dispatch(beginFetch(GET_PLAN_ID));

    // ErrorStateがプラン情報取得だった場合はリセットする
    if (getState().app.error.requestId === GET_PLAN_ID) {
      dispatch(clearErrors());
    }

    // 国が選択されていなければ後続の処理を行わない
    // (countryが空又はdummyが指定されている場合は未選択と判断)
    if (!country || country === VALUE_DUMMY_REGION) {
      dispatch(endFetch(GET_PLAN_ID));
      return;
    }
    // プラン情報取得APIを実行
    const requestData = getPlanRequest(
      planId,
      country,
      language,
      state,
      serviceId
    );
    const response = await request(
      requestData.url,
      requestData.config,
      false,
      false
    );
    // API実行結果がエラーかどうか判断
    if (response.error) {
      // エラー処理
      const error = response.error as ErrorResponse;
      error.requestId = GET_PLAN_ID;
      dispatch(fetchFailure(error));
      dispatch(failedFetch(GET_PLAN_ID));
      return;
    } else {
      // APIが成功した場合は取得したプラン情報でstateを更新
      const body: GetPlanResponse = (response.body as unknown) as GetPlanResponse;
      // 取得したプラン情報でstateを更新
      dispatch(fetchSuccess([body], modelName, type));
    }
    // GET_PLAN_IDを未処理に変更
    dispatch(endFetch(GET_PLAN_ID));
  };
};

/**
 * プラン情報をクリアする
 */
export const reset = () => {
  return (dispatch: ThunkDispatch<Record<string, unknown>, void, any>) => {
    dispatch(resetState());
  };
};

/**
 * プラン情報
 */
export interface PlanInformation {
  /** プランID */
  planId: string;
  /** プラン名 */
  name: string;
  /** 説明 */
  description: string;
  /** 画像URL */
  imageUrl: string;
  /** サービス情報のリスト */
  services: ServiceInformation[];
  /** 有効期間(31日 or 365日 or なし) */
  validityPeriod: number | null;
  /** 利用制限のある売り切りプランの有効期限（日単位。未設定の場合は空文字） */
  perpetualValidityPeriod: number | null;
  /** 価格 */
  prices: pricesInformation[];
  /** 静的コンテンツへのリンク */
  links: LinksInformation[];
  /** 税コード */
  taxCode: string;
  /** 状態("inactive"：無効 / "active"：有効 / "stop"：新規購入停止) */
  state: PlanStateType;
  /** デバイス制限種別(1が設定されている場合、デバイス制限が可能。2の場合はカメラ有償機能プラン) */
  deviceLimitType: number | null;
  /** サポートデバイスの機種ID(deviceLimitTypeが2以外の時は空配列) */
  devices: string[];
}

/**
 * サービス情報
 */
export interface ServiceInformation {
  /** サービスID */
  serviceId: string;
  /** 使用上限回数 */
  usageLimit: number;
  /** サービス名 */
  name: string;
  /** 画像URL */
  imageUrl: string;
  /** 説明 */
  description: string;
  /** サービスの詳細 */
  details: string;
  /** 状態("active": 有効) */
  state: ServiceStateType;
}

/**
 * 価格情報
 */
interface pricesInformation {
  /** 価格 */
  price: number;
  /** 税込価格 */
  taxIncludedPrice: number;
  /** 通貨 */
  currency: string;
  /** 決済ベンダー */
  payment: string;
  /** 決済ベンダー商品ID */
  paymentProductId: string;
}

/**
 * リンク情報
 */
export interface LinksInformation {
  /** リンクURLの表示文字列 */
  title: string;
  /** リンクURL */
  url: string;
}

/**
 * プラン情報のState
 * キーにプランID、値にプラン情報を持つ
 */
export interface PlanState {
  [key: string]: PlanInformation;
}

export interface PlanInitialState {
  planList: PlanState;
  initialPlanList: PlanState;
}

const initialState: PlanInitialState = {
  planList: {},
  initialPlanList: {}
};

export default (
  state: PlanInitialState = initialState,
  actions: PlanAction
): PlanInitialState => {
  switch (actions.type) {
    case ActionTypes.FETCH_SUCCESS: {
      const newState: PlanState = {};
      Object.keys(state.initialPlanList).forEach((key) => {
        if (key in actions.payload.planState) {
          newState[key] = actions.payload.planState[key];
        } else {
          newState[key] = state.initialPlanList[key];
        }
      });
      Object.keys(actions.payload.planState).forEach((key) => {
        if (!(key in newState)) {
          newState[key] = actions.payload.planState[key];
        }
      });

      // 取得したデータ+既存データに対して絞り込みを実施する
      const filteredPlanList = filterList(
        newState,
        actions.payload.modelName,
        actions.payload.type
      );

      // 画面に表示するplanListは絞り込み後のデータで、初期値を保持したいinitialPlanListはapiのレスポンスのデータで更新する
      return { planList: filteredPlanList, initialPlanList: newState };
    }
    case ActionTypes.RESET_STATE: {
      return initialState;
    }
    case ActionTypes.FILTER_STATE: {
      const newState = filterList(
        state.initialPlanList,
        actions.payload.modelName,
        actions.payload.type
      );
      return { ...state, planList: newState };
    }
    default: {
      return state;
    }
  }
};
